import { useTheme } from '@/contexts/ThemeContext';
import { useLocation } from 'react-router-dom';

const WaveDivider = ({ 
  variant = 'smooth',
  flip = false,
  fromColor,
  toColor,
  className = '',
  height = 'h-16 sm:h-24'
}) => {
  const { pageStyles } = useTheme();
  const location = useLocation();
  
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/map') return 'map';
    if (path === '/dive-centers') return 'dive_centers';
    if (path === '/packages') return 'packages';
    if (path === '/gallery') return 'gallery';
    if (path === '/reviews') return 'reviews';
    if (path === '/contact') return 'contact';
    return 'home';
  };
  
  const body = pageStyles[getPageName()]?.body;
  const isDark = body?.text_color === '#e2e8f0' || body?.text_color === '#ffffff';
  const waveEnabled = body?.wave_decorations === true;
  
  if (!waveEnabled) return null;
  
  const bgColor = toColor || body?.background_value || '#0A0A14';
  const accent = body?.accent_color_secondary || '#A285D4';
  
  const waves = {
    smooth: (
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" 
        className="w-full h-full block" preserveAspectRatio="none">
        <path d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z" 
          fill={bgColor} />
        <path d="M0,80 C200,40 400,100 600,60 C800,20 1000,100 1200,60 C1300,40 1380,70 1440,80 L1440,120 L0,120 Z" 
          fill={bgColor} opacity="0.5" />
      </svg>
    ),
    choppy: (
      <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" 
        className="w-full h-full block" preserveAspectRatio="none">
        <path d="M0,50 Q120,10 240,50 T480,50 T720,50 T960,50 T1200,50 T1440,50 L1440,100 L0,100 Z" 
          fill={bgColor} />
        <path d="M0,65 Q90,35 180,65 T360,65 T540,65 T720,65 T900,65 T1080,65 T1260,65 T1440,65 L1440,100 L0,100 Z" 
          fill={bgColor} opacity="0.4" />
        <path d="M0,75 Q60,55 120,75 T240,75 T360,75 T480,75 T600,75 T720,75 T840,75 T960,75 T1080,75 T1200,75 T1320,75 T1440,75 L1440,100 L0,100 Z" 
          fill={bgColor} opacity="0.2" />
      </svg>
    ),
    tentacle: (
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" 
        className="w-full h-full block" preserveAspectRatio="none">
        <path d="M0,80 C160,40 320,100 480,60 C640,20 800,90 960,50 C1120,10 1280,80 1440,40 L1440,120 L0,120 Z" 
          fill={bgColor} />
        <path d="M0,100 C80,70 160,110 240,85 C320,60 400,100 480,80 C560,60 640,95 720,75 C800,55 880,90 960,70 C1040,50 1120,85 1200,65 C1280,45 1360,80 1440,60 L1440,120 L0,120 Z" 
          fill={accent} opacity="0.06" />
      </svg>
    ),
    minimal: (
      <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" 
        className="w-full h-full block" preserveAspectRatio="none">
        <path d="M0,30 C360,60 720,0 1080,30 C1260,45 1380,35 1440,30 L1440,60 L0,60 Z" 
          fill={bgColor} />
      </svg>
    )
  };
  
  return (
    <div 
      className={`relative w-full overflow-hidden ${height} ${className}`}
      style={{ 
        marginTop: '-1px',
        transform: flip ? 'scaleY(-1)' : 'none'
      }}
      aria-hidden="true"
      data-testid="wave-divider"
    >
      {waves[variant] || waves.smooth}
    </div>
  );
};

export default WaveDivider;
